import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  History,
  Search,
  Calendar,
  MapPin,
  Filter,
  Download,
  ChevronRight,
  Clock,
  Users,
  CheckCircle,
  AlertTriangle,
  Flame,
  Droplets,
  Mountain,
  Wind,
  Building2,
  FileText,
  BookOpen,
  Star,
  Share2
} from 'lucide-react';

interface HistoricalCase {
  id: string;
  title: string;
  location: string;
  date: string;
  type: 'flood' | 'fire' | 'earthquake' | 'cyclone' | 'collapse' | 'other';
  severity: 'critical' | 'high' | 'medium' | 'low';
  casualties: number;
  rescued: number;
  responseTime: string;
  resources: {
    boats?: number;
    teams?: number;
    helicopters?: number;
    medical?: number;
  };
  lessons: string[];
  outcome: string;
  verified: boolean;
}

const HistoricalCases: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedCase, setSelectedCase] = useState<HistoricalCase | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const [cases] = useState<HistoricalCase[]>([
    {
      id: '1',
      title: 'Coastal City Flood 2024',
      location: 'Zone A, Coastal Region',
      date: 'Jan 15, 2024',
      type: 'flood',
      severity: 'critical',
      casualties: 1247,
      rescued: 892,
      responseTime: '47 min',
      resources: { boats: 45, teams: 23, helicopters: 8, medical: 120 },
      lessons: [
        'Early warning systems reduced casualties by 60%',
        'Boat deployment needs to be pre-positioned',
        'Medical supplies should be stockpiled in elevated areas'
      ],
      outcome: 'Successful evacuation of 90% of affected population',
      verified: true
    },
    {
      id: '2',
      title: 'Mountain Wildfire 2023',
      location: 'Zone B, Forest Area',
      date: 'Aug 22, 2023',
      type: 'fire',
      severity: 'critical',
      casualties: 345,
      rescued: 567,
      responseTime: '32 min',
      resources: { teams: 34, helicopters: 12, medical: 45 },
      lessons: [
        'Aerial support crucial for fire containment',
        'Evacuation routes need to be pre-planned',
        'Wind direction monitoring essential'
      ],
      outcome: 'Contained after 72 hours, 15,000 acres burned',
      verified: true
    },
    {
      id: '3',
      title: 'Earthquake Zone C 2023',
      location: 'Zone C, Urban Center',
      date: 'Mar 10, 2023',
      type: 'earthquake',
      severity: 'high',
      casualties: 2341,
      rescued: 1456,
      responseTime: '25 min',
      resources: { teams: 67, helicopters: 15, medical: 230 },
      lessons: [
        'Building codes need to be updated',
        'Search and rescue teams need specialized training',
        'Communication infrastructure must be redundant'
      ],
      outcome: '80% of trapped people rescued within 48 hours',
      verified: true
    },
    {
      id: '4',
      title: 'Cyclone Harbor 2022',
      location: 'Zone D, Harbor Area',
      date: 'Oct 05, 2022',
      type: 'cyclone',
      severity: 'high',
      casualties: 892,
      rescued: 567,
      responseTime: '55 min',
      resources: { boats: 34, teams: 23, helicopters: 6, medical: 89 },
      lessons: [
        'Storm surge predictions need improvement',
        'Coastal evacuation shelters must be strengthened',
        'Maritime assets require secure harbor'
      ],
      outcome: 'Successful evacuation of 75% of coastal residents',
      verified: true
    },
    {
      id: '5',
      title: 'Building Collapse Downtown 2022',
      location: 'Zone E, Business District',
      date: 'Jun 18, 2022',
      type: 'collapse',
      severity: 'critical',
      casualties: 234,
      rescued: 89,
      responseTime: '18 min',
      resources: { teams: 45, medical: 67 },
      lessons: [
        'Structural monitoring systems need implementation',
        'Rapid response teams critical for survival',
        'Heavy equipment must be readily available'
      ],
      outcome: '67% of trapped individuals rescued',
      verified: true
    }
  ]);

  const getTypeIcon = (type: string) => {
    switch(type) {
      case 'flood': return <Droplets size={16} className="text-blue-500" />;
      case 'fire': return <Flame size={16} className="text-orange-500" />;
      case 'earthquake': return <Mountain size={16} className="text-brown-500" />;
      case 'cyclone': return <Wind size={16} className="text-purple-500" />;
      case 'collapse': return <Building2 size={16} className="text-red-500" />;
      default: return <AlertTriangle size={16} className="text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch(severity) {
      case 'critical': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 border-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-green-500 border-green-500 bg-green-500/10';
      default: return 'text-gray-500';
    }
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || c.type === selectedType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <History className="text-yellow-500 animate-pulse" />
            HISTORICAL CASE LOOKUP
          </h2>
          <p className="text-xs text-gray-400 mt-1">Learn from past disasters • 847 cases analyzed</p>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="bg-gray-800 hover:bg-gray-700 p-2 rounded-lg"
        >
          <Filter size={18} className="text-gray-400" />
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by title or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-black/50 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm"
          />
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedType('all')}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    selectedType === 'all'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  All Types
                </button>
                {['flood', 'fire', 'earthquake', 'cyclone', 'collapse'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`px-3 py-1 rounded-lg text-sm flex items-center gap-1 ${
                      selectedType === type
                        ? 'bg-cyan-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {getTypeIcon(type)}
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Cases List */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scroll">
        {filteredCases.map((caseItem) => (
          <motion.div
            key={caseItem.id}
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-black/30 rounded-lg p-4 border border-gray-700 hover:border-cyan-500/50 cursor-pointer transition-all"
            onClick={() => setSelectedCase(caseItem)}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                {getTypeIcon(caseItem.type)}
                <h3 className="font-bold text-sm">{caseItem.title}</h3>
                {caseItem.verified && <CheckCircle size={12} className="text-green-500" />}
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(caseItem.severity)}`}>
                {caseItem.severity}
              </span>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
              <div className="flex items-center gap-1">
                <Calendar size={12} />
                {caseItem.date}
              </div>
              <div className="flex items-center gap-1">
                <MapPin size={12} />
                {caseItem.location}
              </div>
              <div className="flex items-center gap-1">
                <Clock size={12} />
                {caseItem.responseTime}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-black/50 p-2 rounded text-center">
                <div className="text-red-500 text-sm font-bold">{caseItem.casualties}</div>
                <div className="text-[10px] text-gray-400">Casualties</div>
              </div>
              <div className="bg-black/50 p-2 rounded text-center">
                <div className="text-green-500 text-sm font-bold">{caseItem.rescued}</div>
                <div className="text-[10px] text-gray-400">Rescued</div>
              </div>
              <div className="bg-black/50 p-2 rounded text-center">
                <div className="text-cyan-500 text-sm font-bold">{caseItem.resources.teams}</div>
                <div className="text-[10px] text-gray-400">Teams</div>
              </div>
            </div>

            <div className="flex justify-end">
              <button className="text-xs text-cyan-500 flex items-center gap-1">
                View Details <ChevronRight size={12} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedCase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedCase(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-gray-900 w-full max-w-2xl rounded-xl border border-cyan-500/30 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-800">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    {getTypeIcon(selectedCase.type)}
                    <h2 className="text-2xl font-bold">{selectedCase.title}</h2>
                  </div>
                  <button
                    onClick={() => setSelectedCase(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar size={14} className="text-gray-400" />
                    <span>{selectedCase.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={14} className="text-gray-400" />
                    <span>{selectedCase.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={14} className="text-gray-400" />
                    <span>Response: {selectedCase.responseTime}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle size={14} className="text-gray-400" />
                    <span>Verified Case</span>
                  </div>
                </div>

                <div className={`inline-block px-3 py-1 rounded-full text-sm ${getSeverityColor(selectedCase.severity)}`}>
                  {selectedCase.severity.toUpperCase()} SEVERITY
                </div>
              </div>

              {/* Stats */}
              <div className="p-6 border-b border-gray-800">
                <h3 className="text-lg font-bold mb-4">Impact Statistics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div className="text-3xl font-bold text-red-500">{selectedCase.casualties}</div>
                    <div className="text-sm text-gray-400">Total Casualties</div>
                  </div>
                  <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="text-3xl font-bold text-green-500">{selectedCase.rescued}</div>
                    <div className="text-sm text-gray-400">Successfully Rescued</div>
                  </div>
                </div>
              </div>

              {/* Resources */}
              <div className="p-6 border-b border-gray-800">
                <h3 className="text-lg font-bold mb-4">Resources Deployed</h3>
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(selectedCase.resources).map(([key, value]) => (
                    <div key={key} className="p-3 bg-black/30 rounded-lg text-center">
                      <div className="text-xl font-bold text-cyan-500">{value}</div>
                      <div className="text-xs text-gray-400 capitalize">{key}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lessons Learned */}
              <div className="p-6 border-b border-gray-800">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <BookOpen size={18} className="text-yellow-500" />
                  Lessons Learned
                </h3>
                <ul className="space-y-3">
                  {selectedCase.lessons.map((lesson, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Star size={14} className="text-yellow-500 mt-0.5" />
                      <span>{lesson}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Outcome */}
              <div className="p-6">
                <h3 className="text-lg font-bold mb-2">Final Outcome</h3>
                <p className="text-sm text-gray-300 mb-4">{selectedCase.outcome}</p>
                
                <div className="flex gap-3">
                  <button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2">
                    <Download size={16} />
                    Download Report
                  </button>
                  <button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2">
                    <Share2 size={16} />
                    Share Case
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center pt-4 border-t border-gray-700/50">
        <div>
          <div className="text-2xl font-bold text-cyan-500">847</div>
          <div className="text-xs text-gray-400">Total Cases</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-green-500">89%</div>
          <div className="text-xs text-gray-400">Success Rate</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-purple-500">47</div>
          <div className="text-xs text-gray-400">This Year</div>
        </div>
      </div>
    </div>
  );
};

export default HistoricalCases;